"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { analytics } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const EXCLUDED_PATH_PATTERN = /^\/admin(\/|$)/;

/**
 * Syncs session identity with analytics destinations, then records page views.
 * Must live under AuthProvider so identify/reset always run before pageView
 * (HubSpot only sends identify on the next trackPageView).
 */
export const AnalyticsTracker = () => {
  const { user, isMePending } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const identityKey = user?.id ?? user?.email ?? "";
  const previousIdentityKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isMePending) {
      return;
    }

    const previousIdentityKey = previousIdentityKeyRef.current;

    if (identityKey) {
      analytics.identify({ email: user.email, id: user.id });
    } else if (previousIdentityKey) {
      analytics.reset();
    }

    previousIdentityKeyRef.current = identityKey;

    if (!pathname || EXCLUDED_PATH_PATTERN.test(pathname)) {
      return;
    }

    const qs = searchParams?.toString();
    analytics.pageView(qs ? `${pathname}?${qs}` : pathname);
  }, [identityKey, isMePending, pathname, searchParams, user?.email, user?.id]);

  return null;
};
